import { WorkedStatus } from "../enums/workedStatus.enum";

interface WorkedStatusParams {
  startAt: string | null;
  endAt: string | null;
  startDate: string;
  endDate: string;
}

interface WorkedStatusResult {
  workedStatus: WorkedStatus;
  totalSeconds: number;
  totalHoursFormatted: string;
  scheduledSeconds: number;
  scheduledHoursFormatted: string;
}

const GRACE_PERIOD_MS = 5 * 60000;

const formatHoursFromMinutes = (minutes: number): string => {
  if (minutes <= 0) return "0h 0m";
  const h = Math.floor(minutes / 60);
  const m = Math.round(minutes % 60);
  if (h === 0) return `${m}m`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
};

const parseDate = (value: string | null): number | null => {
  if (!value) return null;
  const ms = new Date(value).getTime();
  return Number.isNaN(ms) ? null : ms;
};

export const getWorkedStatus = ({
  startAt,
  endAt,
  startDate,
  endDate,
}: WorkedStatusParams): WorkedStatusResult => {
  const now = Date.now();
  const startDateMs = parseDate(startDate);
  const endDateMs = parseDate(endDate);

  const scheduledSeconds =
    startDateMs !== null && endDateMs !== null && endDateMs > startDateMs
      ? Math.round((endDateMs - startDateMs) / 1000)
      : 0;
  const scheduledMinutes = scheduledSeconds / 60;
  const scheduledHoursFormatted = formatHoursFromMinutes(scheduledMinutes);

  const hasStart = !!startAt;
  const hasEnd = !!endAt;

  if (!hasStart && !hasEnd) {
    return {
      workedStatus: WorkedStatus.NOT_STARTED,
      totalSeconds: 0,
      totalHoursFormatted: "0h 0m",
      scheduledSeconds,
      scheduledHoursFormatted,
    };
  }

  const startMs = parseDate(startAt);
  const endMs = parseDate(endAt);

  if (hasStart && !hasEnd) {
    if (startMs === null) {
      return {
        workedStatus: WorkedStatus.INVALID,
        totalSeconds: 0,
        totalHoursFormatted: "-",
        scheduledSeconds,
        scheduledHoursFormatted,
      };
    }
    const isPastSchedule = endDateMs !== null && now > endDateMs + GRACE_PERIOD_MS;
    return {
      workedStatus: isPastSchedule ? WorkedStatus.MISSING_CHECKOUT : WorkedStatus.IN_PROGRESS,
      totalSeconds: 0,
      totalHoursFormatted: "In progress",
      scheduledSeconds,
      scheduledHoursFormatted,
    };
  }

  if (!hasStart && hasEnd) {
    return {
      workedStatus: WorkedStatus.INVALID,
      totalSeconds: 0,
      totalHoursFormatted: "-",
      scheduledSeconds,
      scheduledHoursFormatted,
    };
  }

  if (startMs === null || endMs === null || endMs <= startMs) {
    return {
      workedStatus: WorkedStatus.INVALID,
      totalSeconds: 0,
      totalHoursFormatted: "-",
      scheduledSeconds,
      scheduledHoursFormatted,
    };
  }

  const totalSeconds = Math.round((endMs - startMs) / 1000);
  const totalHoursFormatted = formatHoursFromMinutes(totalSeconds / 60);

  const isLateStart = startDateMs !== null && startMs > startDateMs + GRACE_PERIOD_MS;
  const isOvertime = endDateMs !== null && endMs > endDateMs + GRACE_PERIOD_MS;

  let finalStatus: WorkedStatus = WorkedStatus.COMPLETED;
  if (isLateStart && isOvertime) {
    finalStatus = WorkedStatus.COMPLETED_LATE;
  } else if (isLateStart) {
    finalStatus = WorkedStatus.COMPLETED_LATE_START;
  } else if (isOvertime) {
    finalStatus = WorkedStatus.COMPLETED_OVERTIME;
  }

  return {
    workedStatus: finalStatus,
    totalSeconds,
    totalHoursFormatted,
    scheduledSeconds,
    scheduledHoursFormatted,
  };
};